import { RouteObject, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute, RoleProtectedRoute } from './ProtectedRoute';
import Layout from '../pages/layout/Layout';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import React from 'react';
import { SuperAdminDomainProtectedRoute } from './SuperAdminDomainProtectedRoute';
import RouteErrorBoundary from '../components/error/RouteErrorBoundary';
// import CustomerManagementTabs from '../pages/customer/AddEditCustomerTabs';
import AddEditCustomerUIForm from '../pages/customer/AddEditCustomerUIForm';
import EditDataAgent from '../pages/DataAgents/EditDataAgent';
// import CustomerManagementTabs from '../pages/customer/AddEditCustomerTabs';

const DebugRoute: React.FC<{ children: JSX.Element; path: string }> = ({ children, path }) => {
  const auth = useSelector((state: RootState) => state.auth);
  return children;
};

// Add lazy loading wrapper with error handling
const lazyLoad = (importFn: () => Promise<any>) => ({
  lazy: async () => {
    try {
      const module = await importFn();
      return { Component: module.default };
    } catch (error) {
      console.error('Failed to load module:', error);
      throw error;
    }
  },
  errorElement: <RouteErrorBoundary />,
});

// Define routes as RouteObject array
const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    ...lazyLoad(() => import('../pages/auth/Login')),
  },
  {
    path: '/forgot-password',
    ...lazyLoad(() => import('../pages/auth/ForgotPassword')),
  },
  {
    path: '/verify-otp',
    ...lazyLoad(() => import('../pages/auth/VerifyOtp')),
  },
  {
    path: '/reset-password/:token/cust-id/:customerId',
    ...lazyLoad(() => import('../pages/auth/ResetPassword')),
  },
  {
    path: '/verify-email/:token/cust-id/:customerId',
    ...lazyLoad(() => import('../pages/auth/VerifyEmail')),
  },
  {
    path: '/activate-account/:token/cust-id/:customerId',
    ...lazyLoad(() => import('../pages/auth/ActivateUser')),
  },
  {
    path: '/auth/callback',
    ...lazyLoad(() => import('../pages/connector/OAuthCallback')),
  },
  // Protected routes with Layout (consolidated structure)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/chat" replace />,
      },
      {
        path: 'dashboard',
        ...lazyLoad(() => import('../pages/Dashboard')),
      },
      {
        path: 'category-management',
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/category/CategoryManagement')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/category/AddEditCategory')),
          },
          {
            path: 'add/:customerId',
            ...lazyLoad(() => import('../pages/category/AddEditCategory')),
          },
          {
            path: 'add/:customerId/:clientId',
            ...lazyLoad(() => import('../pages/category/AddEditCategory')),
          },
          {
            path: 'edit/:categoryId',
            ...lazyLoad(() => import('../pages/category/AddEditCategory')),
          },
        ],
      },
      // Customer Management route
      {
        path: 'customer-management',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin']}>
            <DebugRoute path="customer-management">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '',
            ...lazyLoad(() => import('../pages/customer/CustomerManagementWrapper')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/customer/AddEditCustomer')),
          },
          {
            path: 'edit/:customerId',
            ...lazyLoad(() => import('../pages/customer/AddEditCustomer')),
          },
        ],
      },

      // Embedding SDK
      {
        path: 'sdk',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="sdk">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            ...lazyLoad(() => import('../pages/sdk/EmbeddingSDK')),
            children: [
              {
                index: true,
                element: <Navigate to="integrations" replace />,
              },
              {
                path: 'integrations',
                ...lazyLoad(() => import('../pages/sdk/IntegrationsManagement')),
              },
              {
                path: 'integration-code',
                ...lazyLoad(() => import('../pages/sdk/SDKCode')),
              },
              {
                path: 'integration-code/:integrationId',
                ...lazyLoad(() => import('../pages/sdk/SDKCode')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/sdk/AddEditIntegrations')),
              },
              {
                path: 'edit/:integrationId',
                ...lazyLoad(() => import('../pages/sdk/AddEditIntegrations')),
              },
              {
                path: 'usage-analytics',
                ...lazyLoad(() => import('../pages/sdk/cards/UsageAnalytics')),
              },
              {
                path: 'usage-analytics/:integrationId',
                ...lazyLoad(() => import('../pages/sdk/cards/UsageAnalytics')),
              },
              {
                path: 'conversations',
                ...lazyLoad(() => import('../pages/sdk/SDKConversations')),
              },
              {
                path: 'conversations/:integrationId',
                ...lazyLoad(() => import('../pages/sdk/SDKConversations')),
              },
            ],
          },
        ],
      },
      {
        path: 'document-dashboard',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="document-dashboard">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/document/DocumentDashboard')),
          },
        ],
      },
      {
        path: 'document-upload',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="document-upload">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: ':clientId',
            ...lazyLoad(() => import('../pages/document/DocumentManagement')),
          },
        ],
      },
      {
        path: 'configurations',
        element: (
          <SuperAdminDomainProtectedRoute>
            <DebugRoute path="configurations">
              <Outlet />
            </DebugRoute>
          </SuperAdminDomainProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/configuration/ConfigurationManagement')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/configuration/AddEditConfiguration')),
          },
          {
            path: 'edit/:configId',
            ...lazyLoad(() => import('../pages/configuration/AddEditConfiguration')),
          },
        ],
      },
      {
        path: 'client-management',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="client-management">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/client/ClientManagementWrapper')),
          },
          {
            path: ':customerId',
            ...lazyLoad(() => import('../pages/client/ClientManagementWrapper')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/client/AddEditClient')),
          },
          {
            path: 'add/:customerId',
            ...lazyLoad(() => import('../pages/client/AddEditClient')),
          },
          {
            path: 'edit/:clientId',
            // ...lazyLoad(() => import('../pages/client/AddEditClient')),
            ...lazyLoad(() => import('../pages/client/AddEditClient')),
          },
        ],
      },
      {
        path: 'user-management',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin', 'User']}>
            <DebugRoute path="user-management">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/user/UserManagementWrapper')),
          },
          {
            path: ':clientId',
            ...lazyLoad(() => import('../pages/user/UserManagementWrapper')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/user/AddEditUser')),
          },
          {
            path: 'edit/:userId',
            ...lazyLoad(() => import('../pages/user/AddEditUser')),
          },
        ],
      },
      {
        path: 'chat',
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/chat/Chat')),
          },
          {
            path: ':chatId',
            ...lazyLoad(() => import('../pages/chat/Chat')),
          },
        ],
      },
      {
        path: 'group-management',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="group-management">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/group/GroupManagement')),
          },
          {
            path: 'add',
            ...lazyLoad(() => import('../pages/group/AddEditGroup')),
          },
          {
            path: 'edit/:groupId',
            ...lazyLoad(() => import('../pages/group/AddEditGroup')),
          },
          {
            path: 'view/:groupId',
            ...lazyLoad(() => import('../pages/group/ViewGroupUsers')),
          },
        ],
      },
      {
        path: 'translations',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin']}>
            <DebugRoute path="translations">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/translations/TranslationEditor')),
          },
        ],
      },
      {
        path: 'llm-configuration',
        element: (
          <SuperAdminDomainProtectedRoute>
            <DebugRoute path="llm-configuration">
              <Outlet />
            </DebugRoute>
          </SuperAdminDomainProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '',
            ...lazyLoad(() => import('../pages/llm/LLMConfiguration')),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/llm/ModelOverview')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/llm/AddEditModel')),
              },
              {
                path: 'edit/:configuration_id',
                ...lazyLoad(() => import('../pages/llm/AddEditModel')),
              },
            ],
          },
        ],
      },
      {
        path: 'token-management',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin']}>
            <DebugRoute path="token-management">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '',
            ...lazyLoad(() => import('../pages/token/TokenManagement')),
            children: [
              {
                index: true,
                element: <Navigate to="token-limits" replace />,
              },
              {
                path: 'token-limits',
                ...lazyLoad(() => import('../pages/token/TokenLimits')),
              },
              {
                path: 'usage-analytics',
                ...lazyLoad(() => import('../pages/token/UsageAnalytics')),
              },
              {
                path: 'usage-history',
                ...lazyLoad(() => import('../pages/token/UsageHistory')),
              },
            ],
          },
        ],
      },
      {
        path: 'agents',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin']}>
            <DebugRoute path="agents">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'agent-management',
            element: (
              <DebugRoute path="agent-management">
                <Outlet />
              </DebugRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/agent/AgentManagement')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/agent/AddEditAgent')),
              },
              {
                path: 'edit/:agentId',
                ...lazyLoad(() => import('../pages/agent/AddEditAgent')),
              },
            ],
          },
        ],
      },

      {
        path: 'data-agents',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="data-agents">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'data-agent-management',
            element: (
              <DebugRoute path="data-agent-management">
                <Outlet />
              </DebugRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/DataAgents/DataAgentManagement')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/DataAgents/AddEditDataAgent')),
              },
              // {
              //   path: 'edit/:dataAgentId',
              //   ...lazyLoad(() => import('../pages/DataAgents/AddEditDataAgent')),
              // },
              {
                path: '/data-agents/data-agent-management/edit/:dataAgentId',
                element: <EditDataAgent />,
              },
            ],
          },
        ],
      },

      {
        path: 'knowledge-agent-management',
        element: (
          <DebugRoute path="knowledge-agent-management">
            <Outlet />
          </DebugRoute>
        ),
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/knowledgeAgent/KnowledgeAgentManagement')),
          },
        ],
      },

      {
        path: 'vaultilake-chat',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="vaultilake-chat">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/vaultilakechat/VaultilakeChat')),
          },
          {
            path: ':chatId',
            ...lazyLoad(() => import('../pages/vaultilakechat/VaultilakeChat')),
          },
          {
            path: 'new',
            ...lazyLoad(() => import('../pages/vaultilakechat/VaultilakeChat')),
          },
        ],
      },

      {
        path: 'vaulticrawl',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <Outlet />
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/vaulticrawl/VaultiCrawlDashboard')),
          },
          {
            path: ':clientId',
            ...lazyLoad(() => import('../pages/vaulticrawl/VaultiCrawlDashboard')),
          },
        ],
      },

      {
        path: 'data-platform',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="data-platform">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/dataPlatform/DataPlatformManagement')),
          },
        ],
      },
      {
        path: 'lakehouses',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="lakehouses">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            // Lakehouse detail page: breadcrumb + Bronze/Silver/Gold tabs layout
            path: ':lakehouseId',
            ...lazyLoad(() => import('../pages/lakehouse/LakehouseDetailPage')),
            children: [
              {
                // Default: go to Bronze overview
                index: true,
                element: <Navigate to="bronze/overview" replace />,
              },
              {
                // Individual layer (bronze | silver | gold) with Overview/DataProcessing sub-tabs
                path: ':layerName',
                ...lazyLoad(() => import('../pages/lakehouse/LakehouseLayerPage')),
                children: [
                  {
                    index: true,
                    element: <Navigate to="overview" replace />,
                  },
                  {
                    path: 'overview',
                    ...lazyLoad(() => import('../pages/lakehouse/dispatchers/LayerOverviewDispatcher')),
                  },
                  {
                    path: 'data-processing',
                    ...lazyLoad(() => import('../pages/lakehouse/dispatchers/LayerDataProcessingDispatcher')),
                  },
                ],
              },
            ],
          },
          {
            path: 'sql-queries',
            ...lazyLoad(() => import('../pages/sqlQueries/SqlQueries')),
          },
          {
            path: 'data-connection-management',
            element: (
              <DebugRoute path="data-connection-management">
                <Outlet />
              </DebugRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/dataConnection/DataConnection')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/dataConnection/AddEditDataConnection')),
              },
              {
                path: 'edit/:connectionId',
                ...lazyLoad(() => import('../pages/dataConnection/AddEditDataConnection')),
              },
            ],
          },
        ],
      },
      {
        path: 'notifications',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin', 'User']}>
            <DebugRoute path="notifications">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'templates',
            element: (
              <RoleProtectedRoute allowedRoles={['SuperAdmin']}>
                <Outlet />
              </RoleProtectedRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/notification/NotificationTemplateManager')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/notification/AddEditTemplate')),
              },
              {
                path: 'edit/:templateId',
                ...lazyLoad(() => import('../pages/notification/AddEditTemplate')),
              },
            ],
          },
          {
            path: 'preferences',
            element: (
              <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin', 'User']}>
                <Outlet />
              </RoleProtectedRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/notification/UserNotificationPreferences')),
              },
            ],
          },
        ],
      },
      {
        path: 'connectors',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin', 'CustomerAdmin', 'ClientAdmin']}>
            <DebugRoute path="connectors">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'connector-management',
            element: (
              <DebugRoute path="connector-management">
                <Outlet />
              </DebugRoute>
            ),
            children: [
              {
                index: true,
                ...lazyLoad(() => import('../pages/connector/ConnectorManagement')),
              },
              {
                path: 'add',
                ...lazyLoad(() => import('../pages/connector/AddEditConnector')),
              },
              {
                path: 'edit/:connectorId',
                ...lazyLoad(() => import('../pages/connector/AddEditConnector')),
              },
            ],
          },
        ],
      },
      {
        path: 'custom-fields',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin']}>
            <DebugRoute path="custom-fields">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/customField/CustomFieldManagement')),
          },
        ],
      },
      {
        path: 'agent-state',
        element: (
          <RoleProtectedRoute allowedRoles={['SuperAdmin']}>
            <DebugRoute path="agent-state">
              <Outlet />
            </DebugRoute>
          </RoleProtectedRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            ...lazyLoad(() => import('../pages/agentState/AgentStateManagement')),
          },
        ],
      },
      {
        path: 'docs',
        element: (
          <DebugRoute path="docs">
            <Outlet />
          </DebugRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="intro" replace />,
          },
          {
            path: ':docId',
            ...lazyLoad(() => import('../pages/docs/VaultiscanDocsPage')),
          },
        ],
      },

      {
        path: '*',
        ...lazyLoad(() => import('../pages/NoPage')),
      },
    ],
  },
];

console.log('Routes configuration loaded successfully');
export default routes;
